<template>
  <div
    ref="root"
    class="searchable-select"
    :class="{ open: isOpen, disabled }"
  >
    <!-- Campo/controle -->
    <div
      class="ss-control"
      role="combobox"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      :aria-disabled="disabled"
      tabindex="0"
      @click="toggle"
      @keydown="onControlKeydown"
    >
      <i v-if="icon" :class="icon" class="ss-icon"></i>

      <!-- Texto do valor selecionado ou input de busca quando aberto -->
      <input
        v-if="isOpen"
        ref="searchInput"
        v-model="query"
        class="ss-search"
        :placeholder="searchPlaceholderText"
        @click.stop
        @keydown="onSearchKeydown"
      />
      <span v-else class="ss-value" :class="{ placeholder: !selectedLabel }">
        {{ selectedLabel || effectivePlaceholder }}
      </span>

      <button
        v-if="clearable && hasValue && !disabled"
        type="button"
        class="ss-clear"
        :title="'Limpar'"
        @click.stop="clear"
      >
        <i class="fas fa-times"></i>
      </button>
      <i class="fas fa-chevron-down ss-caret" :class="{ rotated: isOpen }"></i>
    </div>

    <!-- Dropdown -->
    <ul v-if="isOpen" class="ss-dropdown" role="listbox">
      <li v-if="filtered.length === 0" class="ss-empty">
        {{ emptyText }}
      </li>
      <li
        v-for="(opt, i) in filtered"
        :key="i"
        role="option"
        class="ss-option"
        :class="{ active: i === highlighted, selected: isSelected(opt) }"
        :aria-selected="isSelected(opt)"
        @click="select(opt)"
        @mouseenter="highlighted = i"
      >
        <span v-html="highlightMatch(opt.label)"></span>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount, useSlots } from 'vue'

/**
 * Select pesquisavel e reutilizavel.
 * Substitui <select> nativo: v-model + lista de opcoes, filtrando enquanto o usuario digita.
 *
 * Dois modos de uso:
 * 1) Por props (recomendado quando ha uma lista de objetos):
 *    <SearchableSelect v-model="x" :options="perfis" option-value="perfilid" option-label="nome" />
 * 2) Drop-in do <select> nativo: basta trocar a tag e manter os <option> como slot.
 *    <SearchableSelect v-model="x"><option v-for="p in perfis" :value="p.id">{{ p.nome }}</option></SearchableSelect>
 *    Opcoes com atributo "disabled" viram o placeholder automaticamente.
 */

type Primitive = string | number | boolean | null
interface Option { value: Primitive; label: string; raw?: any }

const slots = useSlots()

// Extrai texto de children de vnode (string, numero ou arvore de vnodes)
const nodeText = (node: any): string => {
  if (node == null || typeof node === 'boolean') return ''
  if (typeof node === 'string') return node
  if (typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(nodeText).join('')
  if (typeof node === 'object' && 'children' in node) return nodeText(node.children)
  return ''
}

// Le os <option> do slot default (modo drop-in). Reativo: a computed re-executa
// quando os dados reativos lidos pela render-function do slot mudam.
const parseSlotOptions = (): { options: Option[]; placeholder: string } => {
  const out: Option[] = []
  let placeholder = ''
  const walk = (nodes: any[]) => {
    for (const n of nodes || []) {
      if (!n || typeof n !== 'object') continue
      if (n.type === 'option') {
        const p = n.props || {}
        const label = nodeText(n.children).trim()
        const isDisabled = 'disabled' in p && p.disabled !== false && p.disabled != null
        if (isDisabled) {
          if (!placeholder) placeholder = label
          continue
        }
        out.push({ value: p.value !== undefined ? p.value : null, label })
      } else if (Array.isArray(n.children)) {
        walk(n.children)
      }
    }
  }
  try { walk(slots.default ? slots.default() : []) } catch { /* sem slot */ }
  return { options: out, placeholder }
}

const props = withDefaults(defineProps<{
  modelValue: Primitive
  options: any[]
  optionValue?: string
  optionLabel?: string
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
  clearable?: boolean
  icon?: string
}>(), {
  optionValue: 'value',
  optionLabel: 'label',
  placeholder: 'Selecione...',
  searchPlaceholder: '',
  emptyText: 'Nenhum resultado',
  disabled: false,
  clearable: false,
  icon: '',
})

const emit = defineEmits<{
  'update:modelValue': [value: Primitive]
  change: [value: Primitive]
}>()

const root = ref<HTMLElement | null>(null)
const searchInput = ref<HTMLInputElement | null>(null)
const isOpen = ref(false)
const query = ref('')
const highlighted = ref(-1)

// Normaliza as opcoes: usa a prop :options se fornecida, senao le os <option> do slot.
const normalized = computed<Option[]>(() => {
  if (props.options && props.options.length) {
    return props.options.map((o) => {
      if (o !== null && typeof o === 'object') {
        return { value: o[props.optionValue], label: String(o[props.optionLabel] ?? ''), raw: o }
      }
      return { value: o as Primitive, label: String(o), raw: o }
    })
  }
  return parseSlotOptions().options
})

// Placeholder efetivo: prop explicita ou o texto da opcao "disabled" do slot
const effectivePlaceholder = computed(() => {
  if (props.placeholder && props.placeholder !== 'Selecione...') return props.placeholder
  const slotPh = parseSlotOptions().placeholder
  return slotPh || props.placeholder
})

const hasValue = computed(() => props.modelValue !== null && props.modelValue !== undefined && props.modelValue !== '')

const selectedLabel = computed(() => {
  const match = normalized.value.find((o) => o.value === props.modelValue)
  return match ? match.label : ''
})

const searchPlaceholderText = computed(() =>
  props.searchPlaceholder || selectedLabel.value || effectivePlaceholder.value
)

// Filtro sem acento e case-insensitive
const normalizeStr = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')

const filtered = computed<Option[]>(() => {
  const q = normalizeStr(query.value.trim())
  if (!q) return normalized.value
  return normalized.value.filter((o) => normalizeStr(o.label).includes(q))
})

const isSelected = (opt: Option) => opt.value === props.modelValue

const highlightMatch = (label: string): string => {
  const q = query.value.trim()
  if (!q) return escapeHtml(label)
  const idx = normalizeStr(label).indexOf(normalizeStr(q))
  if (idx === -1) return escapeHtml(label)
  const before = escapeHtml(label.slice(0, idx))
  const match = escapeHtml(label.slice(idx, idx + q.length))
  const after = escapeHtml(label.slice(idx + q.length))
  return `${before}<mark>${match}</mark>${after}`
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

const open = async () => {
  if (props.disabled) return
  isOpen.value = true
  query.value = ''
  highlighted.value = filtered.value.findIndex(isSelected)
  await nextTick()
  searchInput.value?.focus()
}

const close = () => {
  isOpen.value = false
  query.value = ''
  highlighted.value = -1
}

const toggle = () => (isOpen.value ? close() : open())

const select = (opt: Option) => {
  emit('update:modelValue', opt.value)
  emit('change', opt.value)
  close()
}

const clear = () => {
  emit('update:modelValue', null)
  emit('change', null)
}

const onControlKeydown = (e: KeyboardEvent) => {
  if (['Enter', ' ', 'ArrowDown'].includes(e.key) && !isOpen.value) {
    e.preventDefault()
    open()
  } else if (e.key === 'Escape') {
    close()
  }
}

const onSearchKeydown = (e: KeyboardEvent) => {
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    highlighted.value = Math.min(highlighted.value + 1, filtered.value.length - 1)
    scrollToHighlighted()
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    highlighted.value = Math.max(highlighted.value - 1, 0)
    scrollToHighlighted()
  } else if (e.key === 'Enter') {
    e.preventDefault()
    const opt = filtered.value[highlighted.value]
    if (opt) select(opt)
  } else if (e.key === 'Escape') {
    e.preventDefault()
    close()
  } else if (e.key === 'Tab') {
    close()
  }
}

const scrollToHighlighted = () => {
  nextTick(() => {
    const el = root.value?.querySelector('.ss-option.active') as HTMLElement | null
    el?.scrollIntoView({ block: 'nearest' })
  })
}

// Reseta o highlight quando a lista filtrada muda
watch(filtered, () => {
  if (highlighted.value >= filtered.value.length) highlighted.value = filtered.value.length - 1
})

// Fecha ao clicar fora
const onClickOutside = (e: MouseEvent) => {
  if (root.value && !root.value.contains(e.target as Node)) close()
}

onMounted(() => document.addEventListener('mousedown', onClickOutside))
onBeforeUnmount(() => document.removeEventListener('mousedown', onClickOutside))
</script>

<style scoped>
/*
 * Estilos alinhados ao design system global (src/assets/style.css):
 * mesma borda (1.5px --border-light), raio (--radius-sm), fonte (0.88rem) e
 * cores de foco/placeholder dos <input class="form-input"> nativos.
 * IMPORTANTE: nao usar --text-muted aqui (no tema e branco, p/ navbar/sidebar).
 */
.searchable-select {
  position: relative;
  width: 100%;
}

.ss-control {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 42px;
  padding: 10px 14px;
  border: 1.5px solid var(--border-light, #e8e0e8);
  border-radius: var(--radius-sm, 6px);
  background: var(--bg-card, #fff);
  color: var(--text-dark, #1a1a2e);
  font-size: 0.88rem;
  cursor: pointer;
  transition: var(--transition, all 0.2s);
  box-sizing: border-box;
}

.ss-control:focus,
.searchable-select.open .ss-control {
  outline: none;
  border-color: var(--primary-color, #613178);
  box-shadow: 0 0 0 3px rgba(97, 49, 120, 0.08);
}

.searchable-select.disabled .ss-control {
  background: var(--bg-light, #f4f6fb);
  color: var(--text-secondary, #64648c);
  cursor: not-allowed;
  opacity: 0.6;
}

.ss-icon {
  color: var(--secondary-color, #8b5a96);
  font-size: 0.85rem;
  flex-shrink: 0;
}

.ss-value {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ss-value.placeholder {
  color: #b0a0b0;
}

.ss-search {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: var(--text-dark, #1a1a2e);
  font-size: inherit;
  font-family: inherit;
  padding: 0;
  min-width: 0;
}

.ss-search::placeholder {
  color: #b0a0b0;
}

.ss-clear {
  background: none;
  border: none;
  color: var(--text-secondary, #64648c);
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  transition: var(--transition, all 0.2s);
}

.ss-clear:hover {
  color: var(--error-color, #ef4444);
  background: var(--bg-light, #f4f6fb);
}

.ss-caret {
  color: var(--secondary-color, #8b5a96);
  font-size: 0.75rem;
  transition: transform 0.2s;
  flex-shrink: 0;
}

.ss-caret.rotated {
  transform: rotate(180deg);
}

.ss-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1000;
  margin: 0;
  padding: 4px;
  list-style: none;
  max-height: 260px;
  overflow-y: auto;
  background: var(--bg-card, #fff);
  border: 1.5px solid var(--border-light, #e8e0e8);
  border-radius: var(--radius-md, 10px);
  box-shadow: var(--shadow-lg, 0 8px 32px rgba(97, 49, 120, 0.12));
}

.ss-option {
  padding: 9px 12px;
  border-radius: var(--radius-sm, 6px);
  cursor: pointer;
  color: var(--text-dark, #1a1a2e);
  font-size: 0.88rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background-color 0.15s;
}

.ss-option.active {
  background: rgba(97, 49, 120, 0.08);
}

.ss-option.selected {
  color: var(--primary-color, #613178);
  font-weight: 600;
}

.ss-option :deep(mark) {
  background: rgba(97, 49, 120, 0.18);
  color: inherit;
  padding: 0 1px;
  border-radius: 2px;
}

.ss-empty {
  padding: 12px;
  text-align: center;
  color: var(--text-secondary, #64648c);
  font-style: italic;
  font-size: 0.86rem;
}
</style>
